import { Dispatch, FC, SetStateAction } from "react";
import { forwardRef, useEffect, useMemo, useState } from "react";
import {
    Stack,
    Button,
    Grid2 as Grid,
    FormControl,
    InputLabel,
    OutlinedInput,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
    Table,
    TableCell,
    Autocomplete,
    Link,
    TextField,
} from "@mui/material";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import FormSection from "../../Forms/FormSection/form-section";
import { cacheService, setupService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DbFilePurgeRuleInfos } from "../../../Core/Api/Types/db-file-purge-rule-infos";
import { PurgeMethod } from "../../../Core/Api/Types/purge-method";
import { DbFormatInfos } from "../../../Core/Api/Types/db-format-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { useTranslation } from "react-i18next";
import DataTable, { CellSize } from "../../Table/table";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridEditSingleSelectCell,
    GridRowParams,
    GridRowsProp,
} from "@mui/x-data-grid";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { GetEnumValueLabelMap } from "../../../Utils/helpers";
import TableCellSelect from "../../Table/TableCellSelect";
import TableCellMultipleSelect from "../../Table/TableCellMultipleSelect/table-cell-multiple-select";
import { CreateNewDbMetadataValue, GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { GetMetadataMap } from "../../../Redux/Reducers/redux-slice-bundle-maker";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { current, produce } from "immer";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import MetadataInputIndex from "../../MetadataInput/metadata-input-index";
import RemoveIcon from "@mui/icons-material/Remove";

interface PurgeMetadataTableProps {
    pr: DbFilePurgeRuleInfos;
    setPR: Dispatch<SetStateAction<DbFilePurgeRuleInfos>>;
}

const PurgeMetadataTable: FC<PurgeMetadataTableProps> = ({ pr, setPR }) => {
    let _rows: GridRowsProp = [];
    const { t } = useTranslation();
    const apiGrid = useGridApiRef();
    const columns: GridColDef[] = [
        {
            headerName: t("common:metadata"),
            field: "metadata",
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            type: "singleSelect",
            renderCell: (data) => (
                <Autocomplete
                    onChange={(event, value) => MetadataChange(data, value)}
                    options={optionsMemo}
                    value={optionsMemo.find((x) => x.id === data.row.id)! as any}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    fullWidth
                    sx={{ mt: "-24px" }}
                    disableClearable={true}
                    renderInput={(params) => <TextField value={data.row.label} {...params} />}
                />
            ),
        },
        {
            headerName: t("common:value"),
            field: "value",
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            type: "singleSelect",
            renderCell: (data) => (
                <>
                    {data.row && data.row.id !== -500 && (
                        <MetadataInputIndex
                            data={optionsMemo.find((x) => x.id === data.row.id)!}
                            value={data.row}
                            onChange={(value, fieldId) => MetadataValueChange(data, value)}
                        />
                    )}
                </>
            ),
        },
        {
            headerName: t("common:remove"),
            field: "remove row",
            minWidth: CellSize.small,
            flex: CellSize.small,
            type: "actions",
            editable: true,
            getActions: (params: GridRowParams) => {
                const actions: any[] = [];
                onRemoveRow &&
                    actions.push(
                        <GridActionsCellItem
                            icon={<RemoveIcon />}
                            label={t("table:cellLabel.removeCastRow")}
                            onClick={() => onRemoveRow(params.row.index)}
                        />
                    );
                return actions;
            },
        },
    ];
    const MetadataValueChange = (data: any, value: any) => {
        var buffer: any = value.value;
        if (value === true || value === false) buffer = value;
        setPR((x) => ({
            ...x,
            objectMetadata: (x.objectMetadata ?? []).map((m, i) => (i === data.id ? { ...m, value: buffer } : m)),
        }));
        console.log(value);
        apiGrid.current.setEditCellValue({ field: data.field, id: data.id, value: value });
    };

    const MetadataChange = (data, value: DbMetadataFieldInfos) => {
        const buffer = CreateNewDbMetadataValue(value.id, value.baseType!, value.tagName!, null);
        setPR((x) => ({ ...x, objectMetadata: (x.objectMetadata ?? []).map((m, i) => (i === data.id ? buffer : m)) }));
        apiGrid.current.setEditCellValue({ field: data.field, id: data.id, value: buffer });
    };

    const optionsMemo = useMemo(
        () =>
            cacheService
                .GetMetadata()
                .filter((meta) =>
                    [
                        BaseMetadataType.DbBoolMetadataValueInfos,
                        BaseMetadataType.DbStringMetadataValueInfos,
                        BaseMetadataType.DbDoubleMetadataValueInfos,
                        BaseMetadataType.DbLongMetadataValueInfos,
                    ].includes(meta.baseType!)
                )
                .sort((a, b) => a!.label!.localeCompare(b.label!)),
        []
    );

    const onRemoveRow = (idRemove: number) => {
        setPR(
            produce((draft) => {
                draft.objectMetadata?.splice(idRemove, 1);
            })
        );
    };
    const onAddRow = () => {
        const nextRow: IDbBaseMetadataValueInfos = {
            creationTime: null,
            fieldLabel: null,
            fieldName: null,
            fieldType: null,
            id: -500,
            targetLayer: ObjectLayer.Asset,
            updateTime: null,
            value: null,
            valueLabel: null,
        };
        setPR(
            produce((draft) => {
                if (!draft.objectMetadata) draft.objectMetadata = [];
                draft.objectMetadata = [...draft.objectMetadata, nextRow];
            })
        );
    };
    return (
        <>
            <DataTable
                noControls
                editMode="cell"
                apiRef={apiGrid}
                columns={columns}
                rows={pr.objectMetadata?.map((x, i) => ({ ...x, index: i })) ?? []}
                getRowId={(row) => row.index}
                pagination={false}
                disableColumnFilter
                disableRowSelectionOnClick
                sx={{ p: 0, m: 0 }}
            />
            {onAddRow && (
                <Button sx={{ width: "150px" }} variant="outlined" onClick={onAddRow}>
                    {t("button:addRow")}
                </Button>
            )}
        </>
    );
};

export default PurgeMetadataTable;
